import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { getMemberNotifications, getActiveChampionshipSeason } from "@/api";

const SAGT_LOGO = "/images/sagt-logo.png";

const STATIC_NAV_LINKS = [
  { label: "Home", path: "/home" },
  { label: "About Us", path: "/about" },
  { label: "Shop", path: "/shop" },
  { label: "Daily Races", path: "/races" },
  { label: "Leaderboard", path: "/leaderboard" },
  { label: "SAGT Rating", path: "/rankings" },
  { label: "__CHAMPIONSHIP__", path: "/sagt-cup" }, // placeholder — replaced at render time with active championship_name
  { label: "SAGT eSports Team", path: "/esports-team" },
  { label: "SAGT Academy", path: "/academy" },
  { label: "Events", path: "/events" },
  { label: "Leagues", path: "/leagues/login" },
  { label: "SAGT Play", path: "/slot-machine" },
  { label: "Register", path: "/register" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [championshipLabel, setChampionshipLabel] = useState("SAGT Cup");
  const location = useLocation();

  // Desktop "Menu" dropdown (replaces the old always-visible scrolling link
  // strip — users weren't discovering there was more to scroll to).
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const desktopBtnRef = useRef(null);
  const desktopMenuRef = useRef(null);
  const [desktopMenuPos, setDesktopMenuPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    const handler = (e) => {
      if (desktopBtnRef.current?.contains(e.target)) return;
      if (desktopMenuRef.current?.contains(e.target)) return;
      setDesktopMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setDesktopMenuOpen(false);
  }, [location.pathname]);

  const toggleDesktopMenu = () => {
    if (!desktopMenuOpen && desktopBtnRef.current) {
      const rect = desktopBtnRef.current.getBoundingClientRect();
      setDesktopMenuPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
    setDesktopMenuOpen((v) => !v);
  };

  // Fetch active championship name once on mount so the menu label can match the active season.
  useEffect(() => {
    let cancelled = false;
    getActiveChampionshipSeason()
      .then((res) => {
        if (cancelled) return;
        const name = res.data?.championship_name;
        if (name && typeof name === "string" && name.trim()) {
          setChampionshipLabel(name.trim());
        }
      })
      .catch(() => {
        /* keep default "SAGT Cup" */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-fetch on route change so renaming reflects immediately after admin updates.
  useEffect(() => {
    if (location.pathname === "/sagt-cup" || location.pathname.startsWith("/admin")) {
      getActiveChampionshipSeason()
        .then((res) => {
          const name = res.data?.championship_name;
          if (name && typeof name === "string" && name.trim()) {
            setChampionshipLabel(name.trim());
          }
        })
        .catch(() => {});
    }
  }, [location.pathname]);

  const NAV_LINKS = STATIC_NAV_LINKS.map((l) =>
    l.label === "__CHAMPIONSHIP__" ? { ...l, label: championshipLabel } : l
  );

  // Check for logged-in member on mount and location change
  useEffect(() => {
    const checkMemberLogin = () => {
      const token = localStorage.getItem("sagt_member_token");
      const storedMember = localStorage.getItem("sagt_member_data");
      
      if (token && storedMember) {
        try {
          // Decode JWT to check expiry (token is base64 encoded)
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expiry = payload.exp * 1000; // Convert to milliseconds
          
          if (Date.now() < expiry) {
            setMemberData(JSON.parse(storedMember));
          } else {
            // Token expired, clear storage
            localStorage.removeItem("sagt_member_token");
            localStorage.removeItem("sagt_member_data");
            setMemberData(null);
          }
        } catch (e) {
          setMemberData(null);
        }
      } else {
        setMemberData(null);
      }
    };
    
    checkMemberLogin();
    
    // Listen for storage changes (e.g., login/logout in another tab)
    window.addEventListener('storage', checkMemberLogin);
    return () => window.removeEventListener('storage', checkMemberLogin);
  }, [location.pathname]);

  const isWorld = (memberData?.account_type || "SAGT_MEMBER") === "SAGT_WORLD";
  const accountDest = isWorld ? "/world" : "/member";

  const handleSignOut = () => {
    localStorage.removeItem("sagt_member_token");
    localStorage.removeItem("sagt_member_data");
    setMemberData(null);
    window.location.href = "/home";
  };

  // Poll member notifications when signed in (red dot next to racing name)
  const refreshUnread = useCallback(async () => {
    if (!memberData) {
      setUnreadCount(0);
      return;
    }
    try {
      const res = await getMemberNotifications();
      setUnreadCount(res.data?.unread_count || 0);
    } catch (e) {
      // Token may be expired/invalid; just clear the dot.
      setUnreadCount(0);
    }
  }, [memberData]);

  useEffect(() => {
    refreshUnread();
    if (!memberData) return undefined;
    const interval = setInterval(refreshUnread, 60000);
    const onUpdated = () => refreshUnread();
    window.addEventListener("sagt:notifications-updated", onUpdated);
    return () => {
      clearInterval(interval);
      window.removeEventListener("sagt:notifications-updated", onUpdated);
    };
  }, [memberData, refreshUnread, location.pathname]);

  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin) return null;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/5" data-testid="navbar">
      <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/home" className="flex items-center gap-3 group z-10" data-testid="navbar-logo">
          <img src={SAGT_LOGO} alt="SAGT" className="w-24 h-12 md:w-32 md:h-14 object-contain mix-blend-screen hover:scale-110 transition-all duration-300" style={{ filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.3))' }} />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden xl:flex items-center gap-2 z-10">
          <button
            ref={desktopBtnRef}
            onClick={toggleDesktopMenu}
            className={`flex items-center gap-1.5 px-3 py-1.5 font-heading text-[11px] uppercase tracking-wider transition-all border whitespace-nowrap ${
              desktopMenuOpen
                ? "text-[#FFB81C] border-[#FFB81C]/50 bg-[#FFB81C]/10"
                : "text-white/60 border-white/15 hover:text-white hover:border-white/30"
            }`}
            data-testid="desktop-menu-toggle-btn"
            aria-label="Toggle navigation menu"
          >
            {desktopMenuOpen ? <X className="w-3.5 h-3.5" /> : <Menu className="w-3.5 h-3.5" />}
            Menu
          </button>
          <div className="flex items-center gap-1 shrink-0">
            <Link
              to={memberData ? accountDest : "/member/login"}
              className={`px-2 2xl:px-3 py-1.5 font-heading text-[10px] 2xl:text-xs uppercase tracking-wider transition-all border flex items-center gap-1 whitespace-nowrap ${
                memberData 
                  ? "text-[#007749] border-[#007749]/50 hover:bg-[#007749] hover:text-white hover:border-[#007749]"
                  : "text-[#FFB81C] border-[#FFB81C]/50 hover:text-black hover:bg-[#FFB81C] hover:border-[#FFB81C]"
              }`}
              data-testid="nav-link-signin"
            >
              {memberData ? (
                <>
                  <User className="w-3 h-3" />
                  <span className="max-w-[80px] lg:max-w-[120px] truncate">{memberData.racing_name}</span>
                  {unreadCount > 0 && (
                    <span
                      className="ml-1 w-2 h-2 rounded-full bg-[#E03C31] shadow-[0_0_8px_rgba(224,60,49,0.6)] animate-pulse"
                      aria-label={`${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`}
                      data-testid="navbar-member-unread-dot"
                    />
                  )}
                </>
              ) : (
                "Sign In"
              )}
            </Link>
            {memberData && (
              <button
                onClick={handleSignOut}
                className="px-2 2xl:px-3 py-1.5 font-heading text-[10px] 2xl:text-xs uppercase tracking-wider transition-all border text-white/50 border-white/15 hover:text-white hover:border-[#E03C31] hover:bg-[#E03C31]/10 whitespace-nowrap"
                data-testid="nav-signout"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>

        {/* Desktop Menu Dropdown — portaled so the navbar's backdrop-blur
            (a containing block for fixed children) can't clip it. */}
        {desktopMenuOpen && createPortal(
          <div
            ref={desktopMenuRef}
            style={{ position: "fixed", top: desktopMenuPos.top, right: desktopMenuPos.right, zIndex: 60 }}
            className="w-[440px] bg-[#0c0c0e] border border-white/10 shadow-2xl shadow-black/60 p-2 grid grid-cols-2 gap-1"
            data-testid="desktop-nav-menu"
          >
            {NAV_LINKS.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setDesktopMenuOpen(false)}
                  className={`px-3 py-2.5 font-heading text-[11px] uppercase tracking-wide transition-colors ${
                    active
                      ? "text-[#FFB81C] bg-[#FFB81C]/10"
                      : "text-white/55 hover:text-white hover:bg-white/[0.06]"
                  }`}
                  data-testid={`desktop-menu-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>,
          document.body
        )}

        {/* Mobile Toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="xl:hidden text-white/60 hover:text-white transition-colors z-20 p-2"
          data-testid="mobile-nav-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu — full-height overlay so the page never peeks through underneath.
          Portaled to <body> so the navbar's backdrop-blur (a containing block for fixed
          children) can't trap/collapse it. Two columns, with Sign In straight after the
          links rather than pinned to the bottom, so it all fits above a phone browser's
          toolbars without scrolling. Scroll stays on only for landscape phones. */}
      {open && createPortal(
        <div
          className="xl:hidden fixed inset-x-0 top-16 bottom-0 bg-black/95 backdrop-blur-xl border-t border-white/5 z-40 overflow-y-auto"
          data-testid="mobile-nav-menu"
        >
          <div className="grid grid-cols-2 gap-x-4 px-4 pt-2">
            {NAV_LINKS.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setOpen(false)}
                  className={`flex items-center min-h-[48px] py-1 font-heading text-[12px] leading-tight uppercase border-b border-white/[0.06] transition-colors ${
                    active ? "text-[#FFB81C]" : "text-white/60 hover:text-white active:text-white"
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>
          <div className="px-4 py-4">
            <Link
              to={memberData ? accountDest : "/member/login"}
              onClick={() => setOpen(false)}
              className={`flex items-center justify-center gap-2 w-full py-3 font-heading text-[13px] uppercase tracking-wider border transition-colors ${
                memberData
                  ? "text-[#007749] border-[#007749]/50 hover:bg-[#007749]/10"
                  : "text-black bg-[#FFB81C] border-[#FFB81C] hover:bg-white"
              }`}
              data-testid="mobile-nav-signin"
            >
              {memberData ? (
                <>
                  <User className="w-4 h-4" />
                  {memberData.racing_name}
                  {unreadCount > 0 && (
                    <span
                      className="ml-1 w-2 h-2 rounded-full bg-[#E03C31] shadow-[0_0_8px_rgba(224,60,49,0.6)] animate-pulse"
                      aria-label={`${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`}
                      data-testid="navbar-mobile-member-unread-dot"
                    />
                  )}
                </>
              ) : (
                "Sign In"
              )}
            </Link>
            {memberData && (
              <button
                onClick={() => { setOpen(false); handleSignOut(); }}
                className="flex items-center justify-center gap-2 w-full py-3 mt-2 font-heading text-[13px] uppercase tracking-wider border text-white/60 border-white/15 hover:text-white hover:border-[#E03C31] hover:bg-[#E03C31]/10 transition-colors"
                data-testid="mobile-nav-signout"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
}
